import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';
import { useCart } from '@context/CartContext';

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [productPhotos, setProductPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const headers = { 'accept-language': language };
        const productData = await get(`/products/${slug}`, headers);
        if (!productData || Object.keys(productData).length === 0) {
          throw new Error('No product data returned from API');
        }
        setProduct(productData);
        setProductPhotos(productData.photos || []);
      } catch (error) {
        console.error('[ProductDetail] Error fetching product details:', error.message);
        setError(language === 'cs' ? 'Nepodařilo se načíst detaily produktu' : 'Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchProductDetails();
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Detail produktu' : 'Product Details'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Detail produktu' : 'Product Details'} />
        <div className="text-white text-center py-20">
          {error || (language === 'cs' ? 'Produkt nenalezen' : 'Product not found')}
        </div>
        <Footer />
      </div>
    );
  }

  const productData = product.translations?.[language] || product.translations?.en || { 
    name: '', 
    description: '', 
    what_to_expect: '', 
    locations: '', 
    availability: '', 
    eligibility: '' 
  };

  const mainPhoto = productPhotos.length > 0 ? productPhotos[0].image_path : product.image_path;
  const isBestseller = product.is_bestseller === 1 || product.is_bestseller === true;
  const isNew = product.is_new === 1 || product.is_new === true;

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={productData.name || (language === 'cs' ? 'Detail produktu' : 'Product Details')} />

      <section className="py-16 bg-dark-gray relative" id={`product-${product.id}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-red-600/5 blur-3xl rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-red-600/5 blur-3xl rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-1/2">
              <div className="relative overflow-hidden rounded-lg shadow-xl h-96">
                <img
                  src={mainPhoto ? `${IMAGE_BASE_URL}${mainPhoto}` : '/images/products/placeholder.jpg'}
                  alt={productData.name || 'Product'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/products/placeholder.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                {isBestseller && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {language === 'cs' ? 'Bestseller' : 'Bestseller'}
                  </div>
                )}
                {isNew && (
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {language === 'cs' ? 'Novinka' : 'New'}
                  </div>
                )}
              </div>
            </div>

            <div className="lg:w-1/2">
              <h2 className="text-3xl font-title font-bold text-white mb-4">
                {productData.name || (language === 'cs' ? 'Bezejmenný produkt' : 'Unnamed Product')}
              </h2>
              <div className="gradient-line mb-6"></div>
              
              {productData.description && (
                <div className="mb-6">
                  <h3 className="text-xl font-title font-bold text-white mb-2">
                    {language === 'cs' ? 'Popis' : 'Description'}
                  </h3>
                  <p className="text-gray-300">{productData.description}</p>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-title font-bold text-white mb-2">
                  {language === 'cs' ? 'Cena' : 'Price'}
                </h3>
                <span className="price-tag">
                  {new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US').format(product.price)}{' '}
                  <span className="currency">{language === 'cs' ? 'Kč' : 'CZK'}</span>
                </span>
              </div>

              {product.features && product.features.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-title font-bold text-white mb-2">
                    {language === 'cs' ? 'Vlastnosti' : 'Features'}
                  </h3>
                  <ul className="features-list text-gray-300 text-sm">
                    {product.features.map((feature, idx) => (
                      <li key={idx}>{feature[language] || feature.en || (language === 'cs' ? 'Není uvedena' : 'Not specified')}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => addToCart(product)}
                className="btn-drift w-full flex justify-center mb-6"
              >
                <i className="fas fa-shopping-cart text-xs mr-2"></i>
                {language === 'cs' ? 'Přidat do košíku' : 'Add to Cart'}
              </button>

              <Link to="/shop" className="inline-flex items-center text-white hover:text-red-500 transition-colors">
                <i className="fas fa-arrow-left mr-2"></i>
                {language === 'cs' ? 'Zpět do e-shopu' : 'Back to Shop'}
              </Link>
            </div>
          </div>

          {productPhotos.length > 1 && (
            <div className="mt-16">
              <h3 className="text-2xl font-title font-bold text-white mb-6">
                {language === 'cs' ? 'Galerie produktu' : 'Product Gallery'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {productPhotos.slice(1).map((photo, index) => (
                  <div key={photo.id} className="overflow-hidden rounded-lg shadow-lg group">
                    <div className="relative h-64">
                      <img
                        src={photo.image_path ? `${IMAGE_BASE_URL}${photo.image_path}` : '/images/products/placeholder.jpg'}
                        alt={`${productData.name || 'Product'} ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/products/placeholder.jpg';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetail;